import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {forkJoin, of, Subscription, throwError} from 'rxjs';
import {WorkflowEditComponent} from '../workflow-edit/workflow-edit.component';
import {WorkflowTestComponent} from '../workflow-test/workflow-test.component';
import {
    ConfiguratorScenarioService,
    DuplicateScenarioRequestDTO,
    EngineScenarioService,
    PageScenarioLogDto,
    ScenarioDTO,
    ScenarioExecutionLog,
    ScenarioLogDto,
    ScenarioVersionLiteDTO,
    TransactionalScenarioProcess,
    UpdateScenarioDTO,
    UpdateScenarioVersionDTO
} from '@tj/tj-dmp-lib-api';
import {MessageService} from 'primeng/api';
import {catchError, switchMap, tap} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import {
    SelectAffectedPartnersComponent
} from '../../../components/select-affected-partners/select-affected-partners.component';
import {AccountService} from '../../../auth/account.service';
import {isAdminOfTenant} from '../../../auth/admin-levels';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {UtilsService} from '../../../services/utils.service';
import {WorkflowEditDraftService} from '../../../services/workflow-edit-draft.service';

@Component({
    selector: 'dmp-workflow-details',
    templateUrl: './workflow-details.component.html',
    styleUrls: ['./workflow-details.component.scss'],
    standalone: false
})
export class WorkflowDetailsComponent implements OnInit, OnDestroy {

    @ViewChild(WorkflowEditComponent) editComponent!: WorkflowEditComponent;
    @ViewChild(WorkflowTestComponent) testComponent!: WorkflowTestComponent;
    @ViewChild(SelectAffectedPartnersComponent) affectedPartnersSettings: SelectAffectedPartnersComponent;

    tenant: string;
    partner: string;
    id: string;
    version: number;
    actual: ScenarioDTO;
    actualVersions: ScenarioVersionLiteDTO[];

    selectedVersion: ScenarioVersionLiteDTO;
    isSelectedVersionLatest = true;

    actualLogs: ScenarioExecutionLog[] = [];
    totalLogElements = 0;
    currentLogPage = 1;
    logPageSize = 10;

    isEditingMode = false
    showVersionHistory = false;
    showEditWarning = false;
    showDuplication = false;
    // Champs propres au Scenario (name/icon/testEmails), partagés par toutes les versions : les
    // modifier ne crée jamais de nouvelle version, donc pas besoin de passer par le mode édition
    // complet (description/canvas) ni son avertissement.
    showQuickEdit = false;
    quickEditForm: UntypedFormGroup;
    // Confirmation à la sauvegarde, uniquement si la modification va réellement forker une nouvelle
    // version (voir willCreateNewVersion) — pas un avertissement générique au clic sur "Editer".
    showForkConfirm = false;
    private pendingEditData: any = null;
    private pendingVersionToSave: number = null;

    loaded = false;

    isAdmin = false;
    affectedPartners: string[] = [];

    duplicateForm: UntypedFormGroup;

    private routeSubscription: Subscription;
    private querySubscription: Subscription;

    constructor(
        private accountService: AccountService,
        private configuratorScenarioService: ConfiguratorScenarioService,
        private engineScenarioService: EngineScenarioService,
        private readonly fb: UntypedFormBuilder,
        private messageService: MessageService,
        private route: ActivatedRoute,
        private router: Router,
        private translate: TranslateService,
        private utilsService: UtilsService,
        private draftService: WorkflowEditDraftService
    ) { }

    ngOnInit(): void {
        this.routeSubscription = this.route.paramMap.subscribe(params => {
            this.tenant = params.get('tenant');
            this.id = params.get('id');
            this.version = +this.route.snapshot.queryParamMap.get('version');
            const action = params.get('action');

            this.isEditingMode = action === 'edit';

            this.accountService.fetch().subscribe(account => {
                this.isAdmin = isAdminOfTenant(account, this.tenant);

                if (this.id && this.tenant) {
                    this.loadVersionData(this.version);
                }
            });
        });
        this.querySubscription = this.route.queryParamMap.subscribe(queryParams => {
            const versionParam = queryParams.get('version');
            if (!versionParam || !this.id || !this.tenant) {
                return;
            }
            const requestedVersion = parseInt(versionParam, 10);
            if (isNaN(requestedVersion)) {
                return;
            }
            if (requestedVersion === this.version && this.loaded) {
                this.updateSelectedVersion();
                return;
            }
            this.loadVersionData(requestedVersion);
        });
    }

    private loadVersionData(versionNumber: number): void {
        this.version = versionNumber;
        this.loaded = false;

        this.configuratorScenarioService.getVersion(this.tenant, this.id, versionNumber).pipe(
            tap(data => {
                this.actual = data;
                this.partner = data.affiliateCode;
                this.affectedPartners = data.affectedPartners ?? [];
            }),
            switchMap(() =>
                forkJoin({
                    versions: this.configuratorScenarioService.listVersions(this.tenant, this.id),
                    logs: this.engineScenarioService.getLogs(
                        this.tenant, this.id, versionNumber,
                        undefined, undefined, undefined, undefined,
                        0, this.logPageSize
                    )
                })
            )
        ).subscribe({
            next: ({ versions, logs }) => {
                const normalizedVersions: ScenarioVersionLiteDTO[] = (versions || []).map(v => ({
                    ...v,
                    createdAt: this.utilsService.normalizeTimestampToDate((v as any)?.createdAt)
                } as ScenarioVersionLiteDTO));

                this.actualVersions = normalizedVersions;
                this.updateSelectedVersion();
                this.actualLogs = this.mapScenarioLogs(logs);
                this.totalLogElements = logs.totalElements || 0;
                this.currentLogPage = 1;
                this.loaded = true;
            },
            error: () => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('workflow.message.error'),
                    detail: this.translate.instant('workflow.error.not-loaded')
                });
            }
        });
    }

    /**
     * Map ScenarioLogDto (from API) to ScenarioExecutionLog (UI model)
     */
    private mapScenarioLogs(page: PageScenarioLogDto): ScenarioExecutionLog[] {
        if (!page || !page.content) {
            return [];
        }

        return page.content.map(log => this.mapScenarioLog(log, page.content));
    }

    /**
     * Map a single ScenarioLogDto to ScenarioExecutionLog
     */
    private mapScenarioLog(log: ScenarioLogDto, allLogsInPage: ScenarioLogDto[]): ScenarioExecutionLog {
        let context = log.contextJson ? JSON.parse(log.contextJson) : undefined;

        if (log.eventCode === 'EMAIL_EVENT' && context) {
            context._isEmailEvent = true;
            context._stepPath = log.stepPath;
            context._scenarioId = log.scenarioId;
            context._version = log.version;
        }

        return {
            id: log.id?.toString(),
            timestamp: log.createdAt ? new Date(log.createdAt) : new Date(),
            message: this.formatLogMessage(log),
            status: this.resolveLogStatus(log, allLogsInPage),
            context: context
        };
    }

    /**
     * Condition logs stay DEBUG in DB; show success when a later log proves the flow moved past that step.
     */
    private resolveLogStatus(log: ScenarioLogDto, allLogsInPage: ScenarioLogDto[]): ScenarioExecutionLog.StatusEnum {
        if (log.eventCode === 'CONDITION_TRUE' || log.eventCode === 'CONDITION_FALSE') {
            return this.conditionWasFollowedByProgress(log, allLogsInPage)
                ? ScenarioExecutionLog.StatusEnum.SUCCESS
                : ScenarioExecutionLog.StatusEnum.STARTED;
        }
        return this.mapLogLevelToStatus(log.level);
    }

    private conditionWasFollowedByProgress(conditionLog: ScenarioLogDto, allLogsInPage: ScenarioLogDto[]): boolean {
        const conditionPath = conditionLog.stepPath;
        if (!conditionPath) {
            return false;
        }
        const conditionTime = conditionLog.createdAt ? new Date(conditionLog.createdAt).getTime() : 0;

        return allLogsInPage.some(other => {
            if (other.id === conditionLog.id) {
                return false;
            }
            if (conditionLog.personalId != null && other.personalId !== conditionLog.personalId) {
                return false;
            }
            const otherTime = other.createdAt ? new Date(other.createdAt).getTime() : 0;
            if (otherTime <= conditionTime) {
                return false;
            }
            if (!other.stepPath) {
                return false;
            }
            return this.isProgressAfterCondition(conditionPath, other.stepPath);
        });
    }

    private isProgressAfterCondition(conditionPath: string, laterPath: string): boolean {
        if (laterPath === conditionPath) {
            return false;
        }
        if (laterPath.startsWith(conditionPath + '.')) {
            return true;
        }
        const parentEnd = conditionPath.lastIndexOf('.');
        if (parentEnd > 0) {
            const parentPrefix = conditionPath.substring(0, parentEnd + 1);
            if (laterPath.startsWith(parentPrefix) && laterPath.length > conditionPath.length) {
                return true;
            }
        }
        if (/^\d+$/.test(conditionPath)) {
            const rootIdx = parseInt(conditionPath, 10);
            const match = /^(\d+)/.exec(laterPath);
            if (match && parseInt(match[1], 10) > rootIdx) {
                return true;
            }
        }
        return false;
    }

    /**
     * Format a readable message from ScenarioLogDto
     */
    private formatLogMessage(log: ScenarioLogDto): string {
        const parts: string[] = [];

        if (log.eventCode) {
            parts.push(`[${log.eventCode}]`);
        }

        if (log.stepPath !== null && log.stepPath !== undefined) {
            parts.push(`Step ${log.stepPath}`);
        }

        if (log.stepType) {
            parts.push(`(${log.stepType})`);
        }

        if (log.message) {
            parts.push(`: ${log.message}`);
        }

        // Pour EMAIL_EVENT, ajouter les statistiques d'envoi
        if (log.eventCode === 'EMAIL_EVENT' && log.contextJson) {
            try {
                const context = JSON.parse(log.contextJson);
                if (context.recipients && Array.isArray(context.recipients)) {
                    const total = context.recipients.length;
                    const successful = context.status === 'success' ? total : 0;
                    parts.push(` ${successful}/${total}`);
                }
            } catch (e) {
                // Ignorer les erreurs de parsing
            }
        }

        // Si aucune information, afficher au moins l'ID
        if (parts.length === 0 && log.id) {
            parts.push(`Log #${log.id}`);
        }

        return parts.join(' ');
    }

    /**
     * Map log level to execution status
     */
    private mapLogLevelToStatus(level?: string): ScenarioExecutionLog.StatusEnum {
        switch (level) {
            case 'ERROR':
                return ScenarioExecutionLog.StatusEnum.FAILED;
            case 'WARN':
                return ScenarioExecutionLog.StatusEnum.PAUSED;
            case 'INFO':
                return ScenarioExecutionLog.StatusEnum.SUCCESS;
            case 'DEBUG':
                return ScenarioExecutionLog.StatusEnum.STARTED;
            default:
                return ScenarioExecutionLog.StatusEnum.STARTED;
        }
    }

    ngOnDestroy(): void {
        if (this.routeSubscription) {
            this.routeSubscription.unsubscribe();
        }
        if (this.querySubscription) {
            this.querySubscription.unsubscribe();
        }
    }

    get latestVersionNumber(): number {
        if (!this.actualVersions?.length) {
            return this.actual?.version ?? this.version;
        }
        return Math.max(...this.actualVersions.map(v => v.version));
    }

    get statusChangedAtDate(): Date | null {
        if (!this.actual?.statusScenarioChangedAt) return null;
        return this.utilsService.normalizeTimestampToDate(this.actual.statusScenarioChangedAt);
    }

    get publishedVersionNumber(): number | null {
        if (!this.actual) {
            return null;
        }
        const status = (this.actual.statusScenario || '').toUpperCase();
        if (status === 'STOPPED' || status === 'ARCHIVE') {
            return null;
        }
        if (this.actual.latestPublishedVersion != null) {
            return this.actual.latestPublishedVersion;
        }
        const published = this.actualVersions?.find(v => v.status === ScenarioVersionLiteDTO.StatusEnum.PUBLISH);
        return published?.version ?? null;
    }

    private updateSelectedVersion(): void {
        if (!this.actual) {
            return;
        }

        const latest = this.latestVersionNumber;
        const versionParam = this.route.snapshot.queryParamMap.get('version');
        if (versionParam) {
            const requestedVersion = parseInt(versionParam, 10);
            this.selectedVersion = this.actualVersions.find(v => v.version === requestedVersion);
            this.isSelectedVersionLatest = requestedVersion === latest;
        } else {
            this.selectedVersion = this.actualVersions.find(v => v.version === latest);
            this.isSelectedVersionLatest = true;
        }

        if (!this.selectedVersion) {
            this.selectedVersion = this.actualVersions.find(v => v.version === latest);
            this.isSelectedVersionLatest = true;
        }

        if (this.selectedVersion && this.actual?.process) {
            this.selectedVersion = {
                ...this.selectedVersion,
                process: this.actual.process
            };
        }
    }

    getStatusValue(status: string): string {
        switch (status) {
            case ScenarioDTO.StatusScenarioEnum.INPROGRESS:
                return 'started';
            case ScenarioDTO.StatusScenarioEnum.ONPAUSE:
                return 'paused';
            case ScenarioDTO.StatusScenarioEnum.STOPPED:
                return 'stopped';
            case ScenarioDTO.StatusVersionEnum.DRAFT:
                return 'draft';
            case ScenarioDTO.StatusVersionEnum.PUBLISH:
                return 'published';
            case ScenarioDTO.StatusVersionEnum.STOPPED:
                return 'stopped';
            default:
                return 'unknown';
        }
    }

    getStatusClass(version: ScenarioVersionLiteDTO): string {
        switch (version.status) {
            case ScenarioDTO.StatusScenarioEnum.INPROGRESS:
                return 'status-started';
            default:
                return 'status-other';
        }
    }

    onView(): void {
        this.router.navigate(['/h/workflow/board', this.tenant, this.id, 'view'], {
            queryParams: { version: this.version }
        });
    }

    onEdit(): void {
        if (!this.isSelectedVersionLatest) {
            return;
        }
        if (this.selectedVersion?.status === ScenarioVersionLiteDTO.StatusEnum.PUBLISH
            && this.actual.statusScenario !== ScenarioDTO.StatusScenarioEnum.STOPPED) {
            this.showEditWarning = true;
        } else {
            this.router.navigate(['/h/workflow', this.tenant, this.id, 'edit'], {
                queryParams: { version: this.selectedVersion.version }
            });
        }
    }

    onEditWorkflow(): void {
        if(this.isEditingMode){
            // Sans ça, name/description/entryPoint pas encore enregistrés seraient perdus au retour
            // sur cette page (changement de route -> composant détruit/recréé).
            if (this.editComponent?.form) {
                this.draftService.save(this.id, this.selectedVersion?.version ?? this.version, this.editComponent.getFormValue());
            }
            this.router.navigate(['/h/workflow/board', this.tenant, this.id, 'edit'], {
                queryParams: { version: this.version }
            });
        }
    }

    openQuickEdit(): void {
        this.quickEditForm = this.fb.group({
            name: [this.actual?.name ?? null, [Validators.required, Validators.maxLength(50)]],
            icon: [this.actual?.icon ?? null],
            testEmails: [this.actual?.testEmails ?? null]
        });
        this.showQuickEdit = true;
    }

    closeQuickEdit(): void {
        this.showQuickEdit = false;
    }

    updateQuickEditIcon(icon: string): void {
        this.quickEditForm.patchValue({ icon });
    }

    saveQuickEdit(): void {
        if (this.quickEditForm.invalid) {
            this.quickEditForm.markAllAsTouched();
            return;
        }
        const value = this.quickEditForm.value;
        const updatedScenario: UpdateScenarioDTO = {
            name: value.name,
            icon: value.icon,
            affectedPartners: this.affectedPartners,
            testEmails: value.testEmails
        };
        // UpdateScenarioDTO ne touche jamais ScenarioVersion (voir UpdateScenarioDTO.updateToEntity,
        // dmp-configurator) : cet appel ne peut jamais créer de nouvelle version, contrairement à
        // onSave() qui transporte aussi description/process.
        this.configuratorScenarioService
            .updateInfo(updatedScenario, this.tenant, this.id, this.selectedVersion?.version ?? this.version)
            .subscribe({
                next: (result) => {
                    this.actual = result;
                    this.showQuickEdit = false;
                    this.messageService.add({
                        severity: 'success',
                        summary: this.translate.instant('workflow.message.success'),
                        detail: this.translate.instant('workflow.message.updated')
                    });
                },
                error: () => {
                    this.messageService.add({
                        severity: 'error',
                        summary: this.translate.instant('workflow.message.error'),
                        detail: this.translate.instant('workflow.error.not-updated')
                    });
                }
            });
    }

    onDuplicate(): void {
        this.showDuplication = true;
        this.duplicateForm = this.fb.group({
            name: [null],
            partner: [null, this.isAdmin ? Validators.nullValidator : Validators.required]
        });
    }

    closeDuplicate(): void {
        this.showDuplication = false;
        this.duplicateForm.reset();
    }

    saveDuplication(): void {
        this.showDuplication = false;
        const value = this.duplicateForm.value;
        const duplicatedScenario: DuplicateScenarioRequestDTO = {
            newName: value.name,
            affiliateCode: this.isAdmin && !value.partner ? "all" : value.partner,
            owner: this.isAdmin ? "admin" : value.partner,
            affectedPartners: value.partner ? [value.partner] : this.affectedPartners
        };
        this.configuratorScenarioService.duplicate(this.tenant, this.partner, this.id, this.selectedVersion.version, duplicatedScenario).subscribe((data) => {
            this.messageService.add({
                severity: 'success',
                summary: this.translate.instant('workflow.message.success'),
                detail: this.translate.instant('workflow.message.duplicated')
            });
            // this.router.navigate(['/h/workflow', this.tenant, this.partner, data.scenarioId, data.version, 'view']);
        });
        this.duplicateForm.reset();
    }

    onSave(): void {
        if (!this.editComponent?.form) {
            this.messageService.add({
                severity: 'warn',
                summary: this.translate.instant('workflow.message.warn'),
                detail: this.translate.instant('common.loading')
            });
            return;
        }
        const editValid = this.editComponent.isFormValid();
        //const testValid = this.testComponent.isFormValid();

        if (editValid) {
            const editData = this.editComponent.getFormValue();
            const versionToSave = this.selectedVersion?.version ?? this.version;

            if (this.willCreateNewVersion(editData)) {
                this.pendingEditData = editData;
                this.pendingVersionToSave = versionToSave;
                this.showForkConfirm = true;
                return;
            }
            this.performSave(editData, versionToSave);
        } else {
            //this.handleValidationErrors(editValid, testValid);
            this.handleValidationErrors(editValid, true);
        }
    }

    // Fork uniquement si : la version éditée est publiée, le scénario tourne, et la description ou
    // le process (entryPoint) a réellement changé — sans cette dernière condition, sauvegarder sans
    // rien changer (ou juste name/icon/testEmails, envoyés séparément par updateInfo) forkait quand
    // même dès que la version était publiée. Un brouillon (DRAFT) ne fork jamais, donc pas de
    // confirmation à montrer non plus dans ce cas.
    private willCreateNewVersion(editData: any): boolean {
        if (this.selectedVersion?.status !== ScenarioVersionLiteDTO.StatusEnum.PUBLISH) {
            return false;
        }
        const status = (this.actual?.statusScenario || '').toUpperCase();
        if (status !== 'INPROGRESS' && status !== 'ONPAUSE') {
            return false;
        }
        const descriptionChanged = (editData.description ?? null) !== (this.actual?.description ?? null);
        let processChanged = false;
        if (this.actual?.type === ScenarioDTO.TypeEnum.TRANSACTIONAL) {
            const currentEntryPoint = (this.actual?.process as TransactionalScenarioProcess)?.entryPoint ?? null;
            processChanged = (editData.entryPoint ?? null) !== currentEntryPoint;
        }
        return descriptionChanged || processChanged;
    }

    closeForkConfirm(): void {
        this.showForkConfirm = false;
        this.pendingEditData = null;
        this.pendingVersionToSave = null;
    }

    confirmFork(): void {
        this.showForkConfirm = false;
        if (this.pendingEditData) {
            this.performSave(this.pendingEditData, this.pendingVersionToSave);
        }
        this.pendingEditData = null;
        this.pendingVersionToSave = null;
    }

    private performSave(editData: any, versionToSave: number): void {
            const updatedScenario: UpdateScenarioDTO = {
                name: editData.name,
                icon: editData.icon,
                affectedPartners: this.affectedPartners,
                testEmails: editData.testEmails,
                allowMultipleEnrollments: editData.allowMultipleEnrollments,
                recordSaleCost: editData.recordSaleCost
            };

            this.configuratorScenarioService
                .updateInfo(updatedScenario, this.tenant, this.id, versionToSave).pipe(
                    catchError(error =>
                        throwError({ stage: 'updateInfo' as const, error })
                    ),
                    switchMap(infoResult => {
                        // description est propre à la version (DMP2-311), toujours envoyée ici, pas
                        // dans updateInfo ; process seulement pour un scénario transactionnel.
                        const updatedVersion: UpdateScenarioVersionDTO = {
                            description: editData.description,
                            ...(this.actual.type === ScenarioDTO.TypeEnum.TRANSACTIONAL ? {
                                process: {
                                    entryPoint: editData.entryPoint,
                                    type: 'TransactionalScenario'
                                } as TransactionalScenarioProcess
                            } : {})
                        };
                        return this.configuratorScenarioService.updateVersion(updatedVersion, this.tenant, this.id, versionToSave).pipe(
                            catchError(error =>
                                throwError({ stage: 'updateVersion' as const, error })
                            )
                        );
                    })
                )
                .subscribe({
                    next: (versionResult) => {
                        this.draftService.clear(this.id, versionToSave);
                        if (versionResult.version !== this.version) {
                            this.messageService.add({
                                severity: 'success',
                                summary: this.translate.instant('workflow.message.success'),
                                detail: this.translate.instant('workflow.message.updated')
                            });
                        } else {
                            this.messageService.add({
                                severity: 'success',
                                summary: this.translate.instant('workflow.message.success'),
                                detail: this.translate.instant('workflow.message.updated')
                            });
                        }
                        this.router.navigate(['/h/workflow', this.tenant, this.id, 'view'], {
                            queryParams: { version: versionResult.version }
                        });
                    },
                    error: (e: { stage: 'updateInfo' | 'updateVersion'; error: any }) => {
                        this.messageService.add({
                            severity: 'error',
                            summary: this.translate.instant('workflow.message.error'),
                            detail: this.translate.instant('workflow.error.not-updated')
                        });
                    }
                });
    }

    onCancel() {
        this.draftService.clear(this.id, this.selectedVersion?.version ?? this.version);
        this.router.navigate(['/h/workflow', this.tenant, this.id, 'view'], {
            queryParams: { version: this.version }
        });
    }

    private buildTestConditions(testData: any) {
        return {
            testEmails: testData.testEmails,
            testDate: new Date(),
            ...(testData.testPeriod && {
                startDate: testData.testPeriod[0],
                endDate: testData.testPeriod[1]
            })
        };
    }

    private handleValidationErrors(editValid: boolean, testValid: boolean): void {
        console.log('Formulaire invalide:');

        if (!editValid) {
            // Errors are shown under each field once touched: no toast for an input error (docs/ui-design-guidelines.md §12).
            this.editComponent?.markFormTouched();
        }
        if (!testValid) {
            console.log('Erreurs dans le formulaire de test');
        }
    }

    /* Modale d'affichage de l'historique des versions du scénario */
    onWorkflowUpdated(scenario: ScenarioDTO): void {
        this.actual = scenario;
        this.reloadVersionsList();
    }

    private reloadVersionsList(): void {
        if (!this.tenant || !this.id) {
            return;
        }
        this.configuratorScenarioService.listVersions(this.tenant, this.id).subscribe({
            next: (versions) => {
                this.actualVersions = (versions || []).map(v => ({
                    ...v,
                    createdAt: this.utilsService.normalizeTimestampToDate((v as any)?.createdAt)
                } as ScenarioVersionLiteDTO));
                this.updateSelectedVersion();
            }
        });
    }

    openVersionHistory(): void {
        this.reloadVersionsList();
        this.showVersionHistory = true;
    }

    closeVersionHistory() {
        this.showVersionHistory = false;
    }

    onVersionClick(versionNumber: number): void {
        this.router.navigate(['/h/workflow', this.tenant, this.id, 'view'], {
            queryParams: { version: versionNumber }
        });
        this.closeVersionHistory(); // Fermer la modal
    }

    /* Modale d'affichage d'avertissement en cas d'édition d'un  scéario activé */
    closeEditWarning(): void {
        this.showEditWarning = false;
    }

    confirmEdit(): void {
        this.showEditWarning = false;
        this.router.navigate(['/h/workflow', this.tenant, this.id, 'edit'], {
            queryParams: { version: this.version }
        });
    }

    updateAffectedPartners(affectedPartners: string[]) {
        this.affectedPartners = affectedPartners;
    }

    onLogPageChange(page: number): void {
        this.currentLogPage = page;
        const pageIndex = page - 1;
        const logVersion = this.selectedVersion?.version ?? this.version;

        this.engineScenarioService.getLogs(
            this.tenant,
            this.id,
            logVersion,
            undefined,
            undefined,
            undefined,
            undefined,
            pageIndex,
            this.logPageSize
        ).subscribe({
            next: (logs) => {
                this.actualLogs = this.mapScenarioLogs(logs);
                this.totalLogElements = logs.totalElements || 0;
            },
            error: () => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('workflow.message.error'),
                    detail: this.translate.instant('workflow.error.logs-not-loaded')
                });
            }
        });
    }
}
